/**
 * SSR/React-Native-safe accessors for browser globals.
 *
 * DBBL is consumed by azul (web), atlas (React Native), and server-rendered
 * contexts, so `window`/`document` may be undefined. These helpers guard access
 * so web-only effects can no-op outside the browser.
 */

export type DocumentLike = {
  documentElement: { dataset: Record<string, string> };
};

export type MatchMediaWindow = {
  matchMedia: (query: string) => { matches: boolean };
};

export const getDocument = (): DocumentLike | undefined => {
  const g = globalThis as { document?: DocumentLike };
  return typeof g.document === "undefined" ? undefined : g.document;
};

export const getMatchMediaWindow = (): MatchMediaWindow | undefined => {
  const g = globalThis as { window?: { matchMedia?: unknown } };
  if (
    typeof g.window === "undefined" ||
    typeof g.window.matchMedia !== "function"
  ) {
    return undefined;
  }
  return g.window as MatchMediaWindow;
};

type HeadElementLike = {
  id: string;
  rel: string;
  href: string;
};

export type HeadDocumentLike = {
  head: { appendChild: (el: HeadElementLike) => unknown };
  createElement: (tag: string) => HeadElementLike;
  getElementById: (id: string) => unknown;
};

export const getHeadDocument = (): HeadDocumentLike | undefined => {
  const g = globalThis as { document?: Partial<HeadDocumentLike> };
  const doc = g.document;
  if (
    typeof doc === "undefined" ||
    !doc.head ||
    typeof doc.createElement !== "function" ||
    typeof doc.getElementById !== "function"
  ) {
    return undefined;
  }
  return doc as HeadDocumentLike;
};

/**
 * Appends `<link id rel="stylesheet" href>` to <head> once. Idempotent on `id`,
 * so re-renders and StrictMode double-mounts never duplicate the link.
 * No-op outside the browser.
 */
export const ensureStylesheetLink = (id: string, href: string): void => {
  const doc = getHeadDocument();
  if (!doc || doc.getElementById(id)) return;
  const link = doc.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  doc.head.appendChild(link);
};

/**
 * Parses the JSON that Django's `json_script` filter rendered into
 * `<script id type="application/json">`. Returns `undefined` outside the
 * browser, when no element has that id, or when its body is not valid JSON.
 */
export const readJsonScript = (id: string): unknown => {
  const g = globalThis as {
    document?: {
      getElementById?: (id: string) => { textContent: string | null } | null;
    };
  };
  const text = g.document?.getElementById?.(id)?.textContent;
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};
